/* eslint-disable @typescript-eslint/explicit-function-return-type */
import {
  EModuleLoadingState,
  EMouflonPlayerEvent,
  EPlaybackState,
  EPlaybackStartType,
  EPlayerType,
  MouflonPlayer,
  TResolutionV2,
} from '@streaming/mouflon-media-player';
import React, { useEffect } from 'react';

import { MMP_VERSION } from '../version';

type TUseMouflonPlayerProps = {
  onPlaying?: () => void;
  onUpdatePlayerResolution?: (e: { width: number; height: number }) => void;
  onTimeUpdate?: () => void;
  onError?: (e: { error: Error; playerType: EPlayerType }) => void;
  onResolutionNotFound?: () => void;
};

export const useMouflonPlayer = (props: TUseMouflonPlayerProps = {}) => {
  const playerRef: React.MutableRefObject<MouflonPlayer | null> =
    React.useRef<MouflonPlayer | null>(null);
  // TODO: Get features from connect message
  const [abrPolicy] = React.useState<string>('client');
  const [smoothQualityChangeSupported] = React.useState<boolean>(true);
  const [resolutions, setResolutions] = React.useState<TResolutionV2[]>([]);
  const [currentResolution, setCurrentResolution] = React.useState<TResolutionV2 | null>(null);
  const [availablePlayerTypes, setAvailablePlayerTypes] = React.useState<EPlayerType[]>([]);
  const [playbackState, setPlaybackState] = React.useState<EPlaybackState>();
  const [playbackStartType, setPlaybackStartType] = React.useState<EPlaybackStartType>(
    EPlaybackStartType.WAITING,
  );
  const [isModuleLoading, setIsModuleLoading] = React.useState<boolean>(false);
  const [videoPlaybackState, setVideoPlaybackState] = React.useState<{
    state?: EPlaybackState;
    isPlaying: boolean;
    isLoading: boolean;
    isErrored: boolean;
  }>({
    isPlaying: false,
    isLoading: false,
    isErrored: false,
  });

  const handleAvailablePlayerTypes = (e: CustomEvent<{ availablePlayerTypes: EPlayerType[] }>) => {
    setAvailablePlayerTypes(e.detail.availablePlayerTypes);
  };

  const handlePlaybackStateChange = (
    e: CustomEvent<{ prevState: EPlaybackState; nextState: EPlaybackState }>,
  ) => {
    const nextState: EPlaybackState = e.detail.nextState;
    setPlaybackState(nextState);
  };

  const handleUpdatePlayerResolution = (e: CustomEvent<{ height: number; width: number }>) => {
    if (props.onUpdatePlayerResolution) {
      props.onUpdatePlayerResolution(e.detail);
    }
  };

  const handleModuleLoadingStateChanged = (e: CustomEvent<{ newState: EModuleLoadingState }>) => {
    setIsModuleLoading(e.detail.newState === EModuleLoadingState.LOADING);
  };

  const handleVideoPlaybackStateChange = (
    e: CustomEvent<{ prevState: EPlaybackState; nextState: EPlaybackState }>,
  ) => {
    const nextState: EPlaybackState = e.detail.nextState;

    const isPlaying: boolean = nextState === EPlaybackState.PLAYING;
    const isLoading: boolean = nextState === EPlaybackState.LOADING;
    const isErrored: boolean = nextState === EPlaybackState.ERROR;

    setVideoPlaybackState({
      state: nextState,
      isPlaying,
      isLoading,
      isErrored,
    });
  };

  const handleResolutionsAvailable = (e: CustomEvent<{ resolutions: TResolutionV2[] }>) => {
    setResolutions(e.detail.resolutions);
  };

  const handlePlaying = (e: CustomEvent<{ isAutoplay?: boolean }>) => {
    if (e.detail.isAutoplay === false) {
      setPlaybackStartType(EPlaybackStartType.MANUAL);
    } else {
      setPlaybackStartType(EPlaybackStartType.AUTO);
    }
  };

  const handleTimeUpdate = () => {
    if (props.onTimeUpdate) {
      props.onTimeUpdate();
    }
  };

  const handleError = ({ detail }: CustomEvent<{ error: Error; playerType: EPlayerType }>) => {
    if (props.onError) {
      props.onError({ error: detail.error, playerType: detail.playerType });
    }
  };

  const handleResolutionNotFound = () => {
    if (props.onResolutionNotFound) {
      props.onResolutionNotFound();
    }
  };

  const handleResolutionSwitched = ({ detail }: CustomEvent<{ resolutionName: string }>) => {
    setCurrentResolution({ name: detail.resolutionName });
  };

  const toggleEvents = (action: 'on' | 'off') => {
    if (playerRef.current) {
      playerRef.current[action](
        EMouflonPlayerEvent.AVAILABLE_PLAYER_TYPES_UPDATED,
        handleAvailablePlayerTypes,
      );
      playerRef.current[action](
        EMouflonPlayerEvent.PLAYBACK_STATE_CHANGED,
        handlePlaybackStateChange,
      );
      playerRef.current[action](
        EMouflonPlayerEvent.VIDEO_PLAYBACK_STATE_CHANGED,
        handleVideoPlaybackStateChange,
      );
      playerRef.current[action](
        EMouflonPlayerEvent.RESOLUTIONS_AVAILABLE,
        handleResolutionsAvailable,
      );
      playerRef.current[action](
        EMouflonPlayerEvent.UPDATE_PLAYER_RESOLUTION,
        handleUpdatePlayerResolution,
      );
      playerRef.current[action](
        EMouflonPlayerEvent.MODULE_LOADING_STATE_CHANGED,
        handleModuleLoadingStateChanged,
      );
      playerRef.current[action](EMouflonPlayerEvent.RESOLUTION_SWITCHED, handleResolutionSwitched);
      playerRef.current[action](EMouflonPlayerEvent.PLAYING, handlePlaying);
      playerRef.current[action](EMouflonPlayerEvent.ERROR, handleError);
      playerRef.current[action](EMouflonPlayerEvent.TIME_UPDATE, handleTimeUpdate);
      playerRef.current[action](EMouflonPlayerEvent.RESOLUTION_NOT_FOUND, handleResolutionNotFound);
    }
  };

  React.useEffect(() => {
    if (playerRef.current) {
      toggleEvents('on');

      return () => {
        toggleEvents('off');
      };
    }
  }, [playerRef.current, handleResolutionNotFound]);

  useEffect(() => {
    const { isPlaying } = videoPlaybackState;

    if (isPlaying && props.onPlaying) {
      props.onPlaying();
    }
  }, [videoPlaybackState]);

  return {
    playerRef,
    availablePlayerTypes,
    playbackState,
    playbackStartType,
    videoPlaybackState: videoPlaybackState.state,
    abrPolicy,
    smoothQualityChangeSupported,
    resolutions,
    isPlaying: videoPlaybackState.isPlaying,
    isLoading: videoPlaybackState.isLoading,
    isErrored: videoPlaybackState.isErrored,
    isModuleLoading,
    currentResolution,
    version: MMP_VERSION,
  } as {
    playerRef: typeof playerRef;
    availablePlayerTypes: typeof availablePlayerTypes;
    playbackState: typeof playbackState;
    playbackStartType: typeof playbackStartType;
    videoPlaybackState: typeof videoPlaybackState.state;
    abrPolicy: typeof abrPolicy;
    smoothQualityChangeSupported: typeof smoothQualityChangeSupported;
    resolutions: TResolutionV2[];
    isPlaying: boolean;
    isLoading: boolean;
    isErrored: boolean;
    isModuleLoading: boolean;
    currentResolution: TResolutionV2 | null;
    version: string;
  };
};
